import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin, Empty, Input, Tree, Tabs, Button } from "antd";
import {
  UserOutlined,
  SearchOutlined,
  PartitionOutlined,
  DownOutlined,
  RightOutlined,
  ClusterOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import { getEmployeeHierarchy } from "../../redux/rtk/features/user/userSlice";
import "./OrganizationStructure.css";

/** Vibrant level colors for chart circles and connectors (attractive, crisp borders) */
const LEVEL_COLORS = [
  "#B5E48C", // apple green
  "#F4A261", // desert orange
  "#F9C74F", // honey yellow
  "#C77DFF", // sweet violet
  "#90DBF4", // ice blue
  "#FF85A1", // strawberry pink
];


function getLevelColor(level) {
  return LEVEL_COLORS[Math.min(level, LEVEL_COLORS.length - 1)];
}

/**
 * Build tree from flat hierarchy list.
 * Roots: only nodes at the minimum EmployeeLevel (0 or 1) with no manager in the list.
 * Everyone else is attached under their manager so we get a proper tree, not a vertical list.
 */
function buildHierarchyTree(flatList) {
  if (!Array.isArray(flatList) || flatList.length === 0) return [];

  const idMap = {};
  flatList.forEach((emp) => {
    const id = String(emp.EmployeeId);
    idMap[id] = { ...emp, children: [] };
  });

  const minLevel = Math.min(
    ...flatList.map((e) => (e.EmployeeLevel != null ? Number(e.EmployeeLevel) : 999))
  );

  const roots = [];
  const orphans = []; // no manager in list but level > minLevel
  flatList.forEach((emp) => {
    const node = idMap[String(emp.EmployeeId)];
    const reportToId = emp.ReportingToId ? String(emp.ReportingToId) : null;
    const level = emp.EmployeeLevel != null ? Number(emp.EmployeeLevel) : 999;
    const hasNoManagerInList = !reportToId || !idMap[reportToId];
    if (hasNoManagerInList && level === minLevel) {
      roots.push(node);
    } else if (reportToId && idMap[reportToId]) {
      idMap[reportToId].children.push(node);
    } else if (hasNoManagerInList && level > minLevel) {
      orphans.push(node);
    }
  });
  roots.push(...orphans);

  // Sort by EmployeeLevel then name (level 0/1 at top, then children by level)
  const sortByLevelThenName = (nodes) =>
    [...nodes].sort((a, b) => {
      const levelA = Number(a.EmployeeLevel) ?? 999;
      const levelB = Number(b.EmployeeLevel) ?? 999;
      if (levelA !== levelB) return levelA - levelB;
      return (a.EmployeeName || "").localeCompare(b.EmployeeName || "");
    });
  const sortTree = (nodes) => {
    const sorted = sortByLevelThenName(nodes);
    sorted.forEach((n) => {
      if (n.children?.length) n.children = sortTree(n.children);
    });
    return sorted;
  };
  return sortTree(roots);
}

/** Convert hierarchy nodes to Ant Design Tree treeData format */
function toTreeData(nodes) {
  if (!nodes || !nodes.length) return [];
  return nodes.map((node) => ({
    key: String(node.EmployeeId),
    title: (
      <div className="org-tree-node-title">
        <span className="org-tree-node-avatar">
          {node.ProfileImage ? (
            <img src={node.ProfileImage} alt="" className="org-tree-avatar-img" />
          ) : (
            <UserOutlined className="org-tree-avatar-icon" />
          )}
        </span>
        <span className="org-tree-node-content">
          <span className="org-tree-node-name">{node.EmployeeName || "—"}</span>
          <span className="org-tree-node-meta">
            {node.DesignationName && (
              <span className="org-tree-node-designation">
                {node.DesignationName}
              </span>
            )}
          </span>
        </span>
      </div>
    ),
    children: node.children?.length ? toTreeData(node.children) : undefined,
  }));
}

function filterTreeBySearch(nodes, search) {
  if (!search || !search.trim()) return nodes;
  const q = search.trim().toLowerCase();
  const matches = (node) => {
    const name = (node.EmployeeName || "").toLowerCase();
    const code = (node.EmployeeCode || "").toLowerCase();
    const designation = (node.DesignationName || "").toLowerCase();
    return name.includes(q) || code.includes(q) || designation.includes(q);
  };
  const filter = (list) => {
    return list.reduce((acc, node) => {
      const childMatch = filter(node.children || []);
      const nodeMatches = matches(node);
      if (nodeMatches || childMatch.length > 0) {
        // When this employee matches, show them and their full subtree (all reports)
        const children = nodeMatches ? (node.children || []) : childMatch;
        acc.push({ ...node, children });
      }
      return acc;
    }, []);
  };
  return filter(nodes);
}

/** Filter flat list by search (name, code, designation) */
function filterFlatBySearch(flatList, search) {
  if (!Array.isArray(flatList)) return [];
  if (!search || !search.trim()) return flatList;
  const q = search.trim().toLowerCase();
  return flatList.filter((emp) => {
    const name = (emp.EmployeeName || "").toLowerCase();
    const code = (emp.EmployeeCode || "").toLowerCase();
    const designation = (emp.DesignationName || "").toLowerCase();
    return name.includes(q) || code.includes(q) || designation.includes(q);
  });
}

/** Group flat list by EmployeeLevel; returns rows [ [level0...], [level1...], ... ] sorted by level */
function groupByEmployeeLevel(flatList) {
  if (!Array.isArray(flatList) || flatList.length === 0) return [];
  const byLevel = {};
  flatList.forEach((emp) => {
    const level = emp.EmployeeLevel != null ? Number(emp.EmployeeLevel) : 0;
    if (!byLevel[level]) byLevel[level] = [];
    byLevel[level].push(emp);
  });
  const levels = Object.keys(byLevel)
    .map(Number)
    .sort((a, b) => a - b);
  return levels.map((level) =>
    [...byLevel[level]].sort((a, b) =>
      (a.EmployeeName || "").localeCompare(b.EmployeeName || "")
    )
  );
}

/** Collect all keys in tree (for expand all) */
function getAllKeys(nodes) {
  const keys = [];
  function walk(list) {
    (list || []).forEach((n) => {
      keys.push(String(n.EmployeeId));
      if (n.children?.length) walk(n.children);
    });
  }
  walk(nodes);
  return keys;
}

/** Collect keys of nodes up to a given level (0 = root only, 1 = root + children, etc.) */
function getKeysUpToLevel(nodes, maxLevel, currentLevel = 0) {
  if (currentLevel > maxLevel) return [];
  const keys = [];
  (nodes || []).forEach((n) => {
    keys.push(String(n.EmployeeId));
    if (n.children?.length) {
      keys.push(...getKeysUpToLevel(n.children, maxLevel, currentLevel + 1));
    }
  });
  return keys;
}

/**
 * Chart view node: top-down layout, children in horizontal row below.
 * When cardOnly is true, only the card is rendered (used in level-based view to keep row fixed).
 */
function OrgChartNode({ node, level, expandedKeys, onToggle, cardOnly = false }) {
  const key = String(node.EmployeeId);
  const hasChildren = node.children && node.children.length > 0;
  const expanded = expandedKeys.includes(key);
  const apiLevel = node.EmployeeLevel != null ? Number(node.EmployeeLevel) : level;
  const color = getLevelColor(apiLevel);

  const card = (
    <div className="org-chart-card">
      <div
        className="org-chart-circle"
        style={{
          "--level-color": color,
          borderColor: color,
        }}
      >
        <span className="org-chart-avatar">
          {node.ProfileImage ? (
            <img src={node.ProfileImage} alt="" className="org-chart-avatar-img" />
          ) : (
            <UserOutlined className="org-chart-avatar-icon" />
          )}
        </span>
        {hasChildren && (
          <button
            type="button"
            className="org-chart-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(key);
            }}
            style={{ backgroundColor: color }}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <DownOutlined /> : <RightOutlined />}
          </button>
        )}
      </div>
      <div className="org-chart-info">
        <div className="org-chart-name">{node.EmployeeName || "—"}</div>
        {node.DesignationName && (
          <div className="org-chart-designation">{node.DesignationName}</div>
        )}
      </div>
    </div>
  );

  if (cardOnly) {
    return (
      <div className="org-chart-node org-chart-node-card-only" data-node-id={key}>
        {card}
      </div>
    );
  }

  return (
    <div className="org-chart-node" data-node-id={key}>
      {card}
      {hasChildren && expanded && (
        <>
          <div
            className="org-chart-connector-v"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
          <div className="org-chart-children-row" style={{ "--level-color": color }}>
            {node.children.map((child) => (
              <div key={child.EmployeeId} className="org-chart-child-branch">
                <div
                  className="org-chart-connector-v-down"
                  style={{ backgroundColor: color }}
                  aria-hidden="true"
                />
                <OrgChartNode
                  node={child}
                  level={level + 1}
                  expandedKeys={expandedKeys}
                  onToggle={onToggle}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Renders only the connector + children row (used below the card in level-based grid so row stays fixed) */
function OrgChartExpandedContent({ node, level, expandedKeys, onToggle }) {
  const apiLevel = node.EmployeeLevel != null ? Number(node.EmployeeLevel) : level;
  const color = getLevelColor(apiLevel);
  if (!node.children?.length) return null;
  return (
    <div className="org-chart-expanded-content">
      <div
        className="org-chart-connector-v"
        style={{ backgroundColor: color }}
        aria-hidden="true"
      />
      <div className="org-chart-children-row" style={{ "--level-color": color }}>
        {node.children.map((child) => (
          <div key={child.EmployeeId} className="org-chart-child-branch">
            <div
              className="org-chart-connector-v-down"
              style={{ backgroundColor: color }}
              aria-hidden="true"
            />
            <OrgChartNode
              node={child}
              level={level + 1}
              expandedKeys={expandedKeys}
              onToggle={onToggle}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Chart view: root at top, children in horizontal rows below with connector lines */
function ChartView({ tree, expandedKeys, onToggle }) {
  return (
    <div className="org-chart-wrap">
      {tree.map((node) => (
        <OrgChartNode
          key={node.EmployeeId}
          node={node}
          level={0}
          expandedKeys={expandedKeys}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

/** Group tree roots by EmployeeLevel for row layout; returns [ [level0 roots], [level1 roots], ... ] */
function groupRootsByLevel(roots) {
  if (!Array.isArray(roots) || roots.length === 0) return [];
  const byLevel = {};
  roots.forEach((node) => {
    const level = node.EmployeeLevel != null ? Number(node.EmployeeLevel) : 0;
    if (!byLevel[level]) byLevel[level] = [];
    byLevel[level].push(node);
  });
  const levels = Object.keys(byLevel)
    .map(Number)
    .sort((a, b) => a - b);
  return levels.map((level) => byLevel[level]);
}

/** Return keys for virtual "Other" nodes (one per orphan level) so they can be expanded by default */
function getOtherLevelKeys(roots) {
  const levelRows = groupRootsByLevel(roots);
  const keys = [];
  for (let i = 1; i < levelRows.length; i++) {
    const row = levelRows[i];
    if (row && row.length > 0) {
      const level =
        row[0].EmployeeLevel != null ? Number(row[0].EmployeeLevel) : i;
      keys.push(`other-level-${level}`);
    }
  }
  return keys;
}

/** Create a virtual "Other" node to group non-reporting (orphan) employees at a level */
function createOtherNode(level, children) {
  const levelKey = `other-level-${level}`;
  return {
    EmployeeId: levelKey,
    EmployeeName: "Other",
    DesignationName: "Non-reporting",
    EmployeeLevel: level,
    children,
  };
}

/**
 * Tree chart with level-based root rows + connecting lines and expand/collapse.
 * "Other" for level N non-reporting employees is shown in the level N-1 row:
 * - Level 1 orphans' Other → in level 0 row; Level 2 orphans' Other → in level 1 row; etc.
 */
function TreeLevelChartView({ tree, expandedKeys, onToggle }) {
  const levelRows = useMemo(() => groupRootsByLevel(tree), [tree]);

  const rowsToRender = useMemo(() => {
    if (!levelRows || levelRows.length === 0) return [];
    const rows = [];
    // Row 0 (display level 0): level 0 roots + "Other" for level 1 orphans
    const row0Nodes = [...(levelRows[0] || [])];
    if (levelRows[1]?.length > 0) {
      row0Nodes.push(createOtherNode(1, levelRows[1]));
    }
    if (row0Nodes.length > 0) {
      rows.push({ displayLevel: 0, nodes: row0Nodes });
    }

    // Rows 1, 2, ...: "Other" for level 2 orphans in level 1 row, level 3 in level 2 row, etc.
    for (let displayLevel = 1; displayLevel < levelRows.length - 1; displayLevel++) {
      const orphanLevel = displayLevel + 1;
      const orphanRow = levelRows[displayLevel + 1];
      if (orphanRow?.length > 0) {
        rows.push({
          displayLevel,
          nodes: [createOtherNode(orphanLevel, orphanRow)],
        });
      }
    }
    return rows;
  }, [levelRows]);

  if (rowsToRender.length === 0) return <div className="org-chart-wrap org-chart-level-based" />;
  return (
    <div className="org-chart-wrap org-chart-level-based">
      {rowsToRender.map((row, rowIndex) => {
        const displayLevel = row.displayLevel;
        const n = row.nodes.length;
        const gridCols = n > 0 ? `repeat(${n}, minmax(min-content, 1fr))` : "1fr";
        return (
          <React.Fragment key={rowIndex}>
            {rowIndex > 0 && (
              <div
                className="org-chart-level-connector"
                style={{
                  backgroundColor: getLevelColor(displayLevel - 1),
                }}
                aria-hidden="true"
              />
            )}
            <div
              className="org-chart-level-row org-chart-level-row-with-tree org-chart-level-row-grid"
              style={{
                gridTemplateColumns: gridCols,
              }}
              {...(rowIndex === 0 ? { "data-level-row": "0" } : {})}
            >
              {row.nodes.map((node, colIndex) => {
                const nodeLevel =
                  node.EmployeeLevel != null
                    ? Number(node.EmployeeLevel)
                    : displayLevel;
                return (
                  <div
                    key={`${node.EmployeeId}-card`}
                    className="org-chart-level-row-cell org-chart-level-row-cell-card"
                    style={{ gridColumn: colIndex + 1, gridRow: 1 }}
                  >
                    <OrgChartNode
                      node={node}
                      level={nodeLevel}
                      expandedKeys={expandedKeys}
                      onToggle={onToggle}
                      cardOnly
                    />
                  </div>
                );
              })}
              {row.nodes.map((node, colIndex) => {
                const nodeLevel =
                  node.EmployeeLevel != null
                    ? Number(node.EmployeeLevel)
                    : displayLevel;
                const expanded =
                  node.children?.length > 0 &&
                  expandedKeys.includes(String(node.EmployeeId));
                return (
                  <div
                    key={`${node.EmployeeId}-exp`}
                    className="org-chart-level-row-cell org-chart-level-row-cell-expanded"
                    style={{ gridColumn: colIndex + 1, gridRow: 2 }}
                  >
                    {expanded && (
                      <OrgChartExpandedContent
                        node={node}
                        level={nodeLevel}
                        expandedKeys={expandedKeys}
                        onToggle={onToggle}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrganizationStructure() {
  const dispatch = useDispatch();
  const {
    hierarchyList,
    hierarchyLoading,
    hierarchyError,
  } = useSelector((state) => state.users);

  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [viewMode, setViewMode] = useState("chart"); // "chart" | "vertical" | "horizontal"
  const [chartZoom, setChartZoom] = useState(70); // default 70%, range 10–150%
  const chartWrapRef = React.useRef(null);
  const lastExpandedKeyRef = React.useRef(null);

  const tree = useMemo(() => buildHierarchyTree(hierarchyList), [hierarchyList]);
  const filteredTree = useMemo(
    () => filterTreeBySearch(tree, search),
    [tree, search]
  );
  const treeData = useMemo(() => toTreeData(filteredTree), [filteredTree]);


  const recenterChart = React.useCallback(() => {
    const el = chartWrapRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    el.scrollLeft = maxScrollLeft > 0 ? maxScrollLeft / 2 : 0;
    el.scrollTop = 0;
  }, []);

  /** Scroll chart so level 0 row center is in the middle of the viewport (used after Expand All) */
  const scrollToCenterLevelZero = React.useCallback(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const level0Row = wrap.querySelector('[data-level-row="0"]');
    if (!level0Row) return;
    wrap.scrollTop = 0;
    const rowRect = level0Row.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const rowCenterInContent = rowRect.left - wrapRect.left + wrap.scrollLeft + rowRect.width / 2;
    const viewCenter = wrap.clientWidth / 2;
    const scrollLeft = Math.max(
      0,
      Math.min(rowCenterInContent - viewCenter, wrap.scrollWidth - wrap.clientWidth)
    );
    wrap.scrollLeft = scrollLeft;
  }, []);

  /** Scroll chart so the given node (by key) is centered in the viewport (used after manual expand) */
  const scrollToCenterNode = React.useCallback((key) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const nodeEl = wrap.querySelector(`[data-node-id="${key}"]`);
    if (!nodeEl) return;
    const nodeRect = nodeEl.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const nodeCenterX = nodeRect.left - wrapRect.left + wrap.scrollLeft + nodeRect.width / 2;
    const nodeCenterY = nodeRect.top - wrapRect.top + wrap.scrollTop + nodeRect.height / 2;
    const viewCenterX = wrap.clientWidth / 2;
    const viewCenterY = wrap.clientHeight / 2;
    const scrollLeft = Math.max(
      0,
      Math.min(nodeCenterX - viewCenterX, wrap.scrollWidth - wrap.clientWidth)
    );
    const scrollTop = Math.max(
      0,
      Math.min(nodeCenterY - viewCenterY, wrap.scrollHeight - wrap.clientHeight)
    );
    wrap.scrollLeft = scrollLeft;
    wrap.scrollTop = scrollTop;
  }, []);

  useEffect(() => {
    dispatch(getEmployeeHierarchy());
  }, [dispatch]);

  useEffect(() => {
    if (viewMode !== "chart") return;
    const el = chartWrapRef.current;
    if (!el) return;
    const resizeObserver = new ResizeObserver(() => {
      recenterChart();
    });
    resizeObserver.observe(el);
    const handleWindowResize = () => recenterChart();
    window.addEventListener("resize", handleWindowResize);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [viewMode, recenterChart]);

  // Initial recenter when chart loads or data changes only (not on zoom – keep user’s scroll position when zooming)
  useEffect(() => {
    if (viewMode === "chart" && filteredTree.length > 0) {
      const t = setTimeout(recenterChart, 100);
      return () => clearTimeout(t);
    }
  }, [viewMode, filteredTree.length, recenterChart]);

  const handleChartZoomIn = () => {
    setChartZoom((z) => Math.min(150, z + 10));
  };
  const handleChartZoomOut = () => {
    setChartZoom((z) => Math.max(10, z - 10));
  };
  const handleChartZoomReset = () => {
    setChartZoom(70); // reset to default view
  };

  // When search changes, expand all nodes so matches are visible (include "Other" nodes)
  useEffect(() => {
    if (search.trim()) {
      setExpandedKeys([
        ...getAllKeys(filteredTree),
        ...getOtherLevelKeys(filteredTree),
      ]);
    }
  }, [search, filteredTree]);

  const onExpand = (keys) => {
    setExpandedKeys(keys);
  };

  const onChartToggle = (key) => {
    setExpandedKeys((prev) => {
      const isExpanding = !prev.includes(key);
      if (isExpanding) lastExpandedKeyRef.current = key;
      return isExpanding ? [...prev, key] : prev.filter((k) => k !== key);
    });
  };

  // When user manually expands a node, recenter so that node stays in the center
  useEffect(() => {
    if (viewMode !== "chart" || !lastExpandedKeyRef.current) return;
    const key = lastExpandedKeyRef.current;
    lastExpandedKeyRef.current = null;
    const t = setTimeout(() => scrollToCenterNode(key), 100);
    return () => clearTimeout(t);
  }, [viewMode, expandedKeys, scrollToCenterNode]);

  const handleExpandAll = () => {
    setExpandedKeys([
      ...getAllKeys(filteredTree),
      ...getOtherLevelKeys(filteredTree),
    ]);
    // After DOM updates, recenter so level 0 middle employee is in view center
    setTimeout(() => scrollToCenterLevelZero(), 150);
  };

  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  return (
    <div className="org-structure-page">
      <div className="org-structure-header">
        <div className="org-structure-header-left">
          <h1 className="org-structure-title">Organization Structure</h1>
          <p className="org-structure-subtitle">Employee hierarchy and reporting structure</p>
        </div>
        <div className="org-structure-header-right">
          <Tabs
            activeKey={viewMode === "horizontal" ? "chart" : viewMode}
            onChange={setViewMode}
            type="line"
            className="org-structure-view-tabs"
            items={[
              {
                key: "chart",
                label: (
                  <span>
                    <ClusterOutlined /> Chart
                  </span>
                ),
              },
              {
                key: "vertical",
                label: (
                  <span>
                    <PartitionOutlined /> Vertical
                  </span>
                ),
              },
            ]}
          />
          <Input
            placeholder="Search by name, code or designation"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="org-structure-search"
          />
          {viewMode === "chart" && (
            <div className="org-structure-chart-toolbar">
              <div className="org-structure-toolbar-group">
                <Button
                  type="text"
                  className="org-structure-toolbar-btn"
                  icon={<VerticalAlignBottomOutlined />}
                  onClick={handleExpandAll}
                  title="Expand all"
                />
                <Button
                  type="text"
                  className="org-structure-toolbar-btn"
                  icon={<VerticalAlignTopOutlined />}
                  onClick={handleCollapseAll}
                  title="Collapse all"
                />
              </div>
              <div className="org-structure-toolbar-divider" />
              <div className="org-structure-toolbar-group">
                <Button
                  type="text"
                  className="org-structure-toolbar-btn"
                  icon={<ZoomOutOutlined />}
                  onClick={handleChartZoomOut}
                  disabled={chartZoom <= 10}
                  title="Zoom out"
                />
                <Button
                  type="text"
                  className="org-structure-toolbar-btn"
                  icon={<ZoomInOutlined />}
                  onClick={handleChartZoomIn}
                  disabled={chartZoom >= 150}
                  title="Zoom in"
                />
              </div>
              <div className="org-structure-toolbar-divider" />
              <div className="org-structure-toolbar-group">
                <Button
                  type="text"
                  className="org-structure-toolbar-btn"
                  icon={<FullscreenOutlined />}
                  onClick={handleChartZoomReset}
                  title="Reset zoom"
                />
                <span className="org-structure-zoom-value">{chartZoom}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {hierarchyLoading && (
        <div className="org-structure-loading">
          <Spin size="large" tip="Loading organization structure..." />
        </div>
      )}

      {!hierarchyLoading && hierarchyError && (
        <Empty
          description={hierarchyError}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className="org-structure-empty"
        />
      )}

      {!hierarchyLoading && !hierarchyError && (!Array.isArray(hierarchyList) || hierarchyList.length === 0) && (
        <Empty
          description="No hierarchy data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className="org-structure-empty"
        />
      )}

      {!hierarchyLoading && !hierarchyError && Array.isArray(hierarchyList) && hierarchyList.length > 0 && (
        <div className="org-structure-tree-wrap">
          {filteredTree.length === 0 ? (
            <Empty
              description="No matches for your search"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              className="org-structure-empty"
            />
          ) : viewMode === "chart" ? (
            <div
              ref={chartWrapRef}
              className="org-structure-chart-wrap"
            >
              <div
                className="org-structure-chart-zoom-inner"
                style={{
                  transform: `scale(${chartZoom / 100})`,
                  transformOrigin: "top center",
                }}
              >
                <TreeLevelChartView
                  tree={filteredTree}
                  expandedKeys={expandedKeys}
                  onToggle={onChartToggle}
                />
              </div>
            </div>
          ) : (
            <Tree
              showLine
              showIcon={false}
              blockNode
              expandedKeys={expandedKeys}
              onExpand={onExpand}
              treeData={treeData}
              className="org-structure-tree"
            />
          )}
        </div>
      )}
    </div>
  );
}
